import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FileText, Loader2, Eye, Trash2, Calendar, Download, Archive, ArchiveRestore, Filter } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { InformeMultiSistemaDialog } from '@/components/anomalias/InformeMultiSistemaDialog';
import { toast } from 'sonner';

interface Props {
  clienteId: string;
  clienteNombre: string;
}

interface InformeRow {
  id: string;
  fecha: string;
  estado: string;
  sistemas_ids: string[];
  sede_id: string | null;
  observaciones_generales: string | null;
  firma_tecnico: string | null;
  firma_cliente: string | null;
  nombre_firmante_cliente: string | null;
  tecnico_id: string;
  pdf_path: string | null;
  archivado: boolean;
  categoria: 'seguridad' | 'pci';
  tecnico_nombre?: string;
}


interface SistemaMin { id: string; tipo: string; categoria: string; ubicacion: string | null; descripcion: string | null; }
interface SedeMin { id: string; nombre: string; direccion: string | null; codigo_postal: string | null; poblacion: string | null; provincia: string | null; }

const tipoRevisionLabel: Record<string, string> = {
  trimestral: 'Trimestral',
  semestral: 'Semestral',
  anual: 'Anual',
};

const estadoBadge: Record<string, { label: string; className: string }> = {
  borrador: { label: 'Borrador', className: 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400 border-yellow-500/30' },
  finalizado: { label: 'Finalizado', className: 'bg-green-500/20 text-green-700 dark:text-green-400 border-green-500/30' },
  completado: { label: 'Finalizado', className: 'bg-green-500/20 text-green-700 dark:text-green-400 border-green-500/30' },
};

const formatMMYYYY = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return `${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
};

export const InformesClienteTab: React.FC<Props> = ({ clienteId, clienteNombre }) => {
  const [informes, setInformes] = useState<InformeRow[]>([]);
  const [tipoRevisionPorInforme, setTipoRevisionPorInforme] = useState<Record<string, string>>({});
  const [sistemas, setSistemas] = useState<SistemaMin[]>([]);
  const [sedes, setSedes] = useState<SedeMin[]>([]);
  const [loading, setLoading] = useState(true);
  const [openInforme, setOpenInforme] = useState<InformeRow | null>(null);
  const [aEliminar, setAEliminar] = useState<string | null>(null);
  const [eliminando, setEliminando] = useState(false);
  const [mostrarArchivados, setMostrarArchivados] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState<string>('');
  const [filtroCategoria, setFiltroCategoria] = useState<'' | 'seguridad' | 'pci'>('');

  const cargar = async () => {
    setLoading(true);
    const [{ data: informesData }, { data: sistemasData }, { data: sedesData }] = await Promise.all([
      supabase
        .from('informes_consolidados')
        .select('id, fecha, estado, sistemas_ids, sede_id, observaciones_generales, firma_tecnico, firma_cliente, nombre_firmante_cliente, tecnico_id, pdf_path, archivado, categoria')
        .eq('cliente_id', clienteId)
        .order('fecha', { ascending: false }),
      supabase
        .from('sistemas')
        .select('id, tipo, categoria, ubicacion, descripcion')
        .eq('cliente_id', clienteId),
      supabase
        .from('cliente_sedes')
        .select('id, nombre, direccion, codigo_postal, poblacion, provincia')
        .eq('cliente_id', clienteId),
    ]);

    setSistemas((sistemasData || []) as SistemaMin[]);
    setSedes((sedesData || []) as SedeMin[]);
    let lista = (informesData || []) as unknown as InformeRow[];

    // Cargar nombres de técnicos
    const tecnicoIds = Array.from(new Set(lista.map((i) => i.tecnico_id).filter(Boolean)));
    if (tecnicoIds.length > 0) {
      const { data: perfilesData } = await supabase
        .from('perfiles')
        .select('user_id, nombre')
        .in('user_id', tecnicoIds);
      const map: Record<string, string> = {};
      (perfilesData || []).forEach((p: any) => { map[p.user_id] = p.nombre; });
      lista = lista.map((i) => ({ ...i, tecnico_nombre: map[i.tecnico_id] }));
    }
    setInformes(lista);


    // Derivar tipo_revision por informe a partir de los checklists de sus sistemas en el mismo mes
    if (lista.length > 0) {
      const allSistemaIds = Array.from(new Set(lista.flatMap((i) => i.sistemas_ids || [])));
      if (allSistemaIds.length > 0) {
        const { data: cks } = await supabase
          .from('checklists_seguridad')
          .select('sistema_id, tipo_revision, fecha')
          .eq('cliente_id', clienteId)
          .in('sistema_id', allSistemaIds);
        const tiposMap: Record<string, string> = {};
        // Precedencia: anual cubre todo, semestral cubre trimestral.
        const PRECEDENCIA: Record<string, number> = { trimestral: 1, semestral: 2, anual: 3 };
        lista.forEach((inf) => {
          const fechaInf = new Date(inf.fecha);
          const keyMes = `${fechaInf.getFullYear()}-${fechaInf.getMonth()}`;
          let mejor: string | null = null;
          (cks || []).forEach((c: any) => {
            if (!inf.sistemas_ids?.includes(c.sistema_id)) return;
            const f = new Date(c.fecha);
            if (`${f.getFullYear()}-${f.getMonth()}` !== keyMes) return;
            const t = c.tipo_revision;
            if (!t || t === 'rapida' || !PRECEDENCIA[t]) return;
            if (!mejor || PRECEDENCIA[t] > PRECEDENCIA[mejor]) mejor = t;
          });
          if (mejor) tiposMap[inf.id] = mejor;
        });
        setTipoRevisionPorInforme(tiposMap);
      } else {
        setTipoRevisionPorInforme({});
      }
    } else {
      setTipoRevisionPorInforme({});
    }
    setLoading(false);
  };

  useEffect(() => { cargar(); }, [clienteId]);

  const handleEliminar = async (id: string) => {
    setEliminando(true);
    try {
      const inf = informes.find((i) => i.id === id);
      if (inf?.pdf_path) {
        await supabase.storage.from('informes-consolidados').remove([inf.pdf_path]);
      }
      const { error } = await supabase.from('informes_consolidados').delete().eq('id', id);
      if (error) throw error;
      await cargar();
    } catch (e) {
      console.error('Error eliminando informe', e);
    } finally {
      setEliminando(false);
      setAEliminar(null);
    }
  };

  const handleAbrirPdf = async (inf: InformeRow) => {
    if (!inf.pdf_path) {
      toast.error('Este informe no tiene PDF guardado. Finalízalo de nuevo para generar el archivo descargable.');
      return;
    }
    const { data, error } = await supabase.storage
      .from('informes-consolidados')
      .createSignedUrl(inf.pdf_path, 60 * 10);
    if (error || !data?.signedUrl) {
      console.error('No se pudo abrir el PDF', error);
      toast.error('No se pudo abrir el PDF guardado.');
      return;
    }
    window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
  };

  const handleToggleArchivar = async (id: string, archivar: boolean) => {
    const { error } = await supabase
      .from('informes_consolidados')
      .update({ archivado: archivar } as any)
      .eq('id', id);
    if (error) {
      console.error('Error archivando informe', error);
      return;
    }
    await cargar();
  };

  const sedeDeInforme = (sedeId: string | null) => sedes.find((s) => s.id === sedeId) || null;

  const informesVisibles = informes.filter(i => {
    const matchArchivado = mostrarArchivados ? i.archivado : !i.archivado;
    if (!matchArchivado) return false;
    if (filtroCategoria && i.categoria !== filtroCategoria) return false;
    if (!filtroTipo) return true;
    const tipo = tipoRevisionPorInforme[i.id];
    return tipo === filtroTipo;
  });

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Informes consolidados
              <Badge variant="secondary" className="ml-2">{informesVisibles.length}</Badge>
            </CardTitle>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1.5">
                <Filter className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground hidden sm:inline">Tipo doc:</span>
                {([
                  { v: '' as const, label: 'Todos' },
                  { v: 'seguridad' as const, label: 'Seguridad' },
                  { v: 'pci' as const, label: 'PCI' },
                ]).map(({ v, label }) => (
                  <Button
                    key={v || 'all-cat'}
                    variant={filtroCategoria === v ? 'default' : 'outline'}
                    size="sm"
                    className="h-7 text-xs px-2.5"
                    onClick={() => setFiltroCategoria(v)}
                  >
                    {label}
                  </Button>
                ))}
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground hidden sm:inline">Revisión:</span>
                {(['', 'trimestral', 'semestral', 'anual'] as const).map((t) => (
                  <Button
                    key={t || 'all'}
                    variant={filtroTipo === t ? 'default' : 'outline'}
                    size="sm"
                    className="h-7 text-xs px-2.5"
                    onClick={() => setFiltroTipo(t)}
                  >
                    {t ? tipoRevisionLabel[t] || t : 'Todos'}
                  </Button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Switch id="inf-archivados" checked={mostrarArchivados} onCheckedChange={setMostrarArchivados} />
                <Label htmlFor="inf-archivados" className="text-sm cursor-pointer">
                  Ver archivados
                </Label>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {informesVisibles.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-muted-foreground">No hay informes consolidados para este cliente</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[120px]">Fecha (MM-AAAA)</TableHead>
                    <TableHead>Tipo doc</TableHead>
                    <TableHead>Tipo revisión</TableHead>
                    <TableHead className="text-center">Sistemas</TableHead>
                    <TableHead>Técnico</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {informesVisibles.map((inf) => {
                    const estado = estadoBadge[inf.estado] || { label: inf.estado, className: '' };
                    const tipo = tipoRevisionPorInforme[inf.id];
                    return (
                      <TableRow key={inf.id}>
                        <TableCell>
                          <div className="flex items-center gap-2 font-mono font-semibold text-base">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            {formatMMYYYY(inf.fecha)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={
                              inf.categoria === 'pci'
                                ? 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30'
                                : 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30'
                            }
                          >
                            {inf.categoria === 'pci' ? 'PCI' : 'Seguridad'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {!tipo ? (
                            <span className="text-xs text-muted-foreground">—</span>
                          ) : (
                            <Badge variant="default" className="capitalize">
                              {tipoRevisionLabel[tipo] || tipo}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline">{inf.sistemas_ids?.length || 0}</Badge>
                        </TableCell>
                        <TableCell>{inf.tecnico_nombre || '—'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={estado.className}>
                            {estado.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {inf.estado !== 'borrador' ? (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleAbrirPdf(inf)}
                                title="Ver PDF guardado"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setOpenInforme(inf)}
                                title="Continuar borrador"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            )}
                            {inf.pdf_path && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleAbrirPdf(inf)}
                                title="Descargar PDF"
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleToggleArchivar(inf.id, !inf.archivado)}
                              title={inf.archivado ? 'Desarchivar' : 'Archivar'}
                            >
                              {inf.archivado ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => setAEliminar(inf.id)}
                              title="Eliminar informe"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {openInforme && (
        <InformeMultiSistemaDialog
          open={!!openInforme}
          onOpenChange={(o) => { if (!o) { setOpenInforme(null); cargar(); } }}
          sistemas={sistemas as any}
          clienteNombre={clienteNombre}
          clienteId={clienteId}
          sede={sedeDeInforme(openInforme.sede_id) || undefined}
          informeId={openInforme.id}
        />
      )}

      <AlertDialog open={!!aEliminar} onOpenChange={(o) => { if (!o) setAEliminar(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar informe</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará permanentemente el informe consolidado. No se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setAEliminar(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => aEliminar && handleEliminar(aEliminar)}
              disabled={eliminando}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {eliminando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
